import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Flex, Input, InputGroup, InputLeftElement, Button, VStack, Text,
  Avatar, Badge, Spinner, useDisclosure, Tag, Center,
} from "@chakra-ui/react";
import { Search, Plus, MessageSquare } from "lucide-react";
import { getCrmContatos } from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";
import NovoContatoModal from "./NovoContatoModal";
import { formatPhone, horaMsg } from "./crmUtils";

const ACCENT = "#F68633";

export default function ConversasTab({ onAbrirChat, refreshKey }) {
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [soNaoLidas, setSoNaoLidas] = useState(false);
  const debounced = useDebounce(search, 400);
  const novo = useDisclosure();

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    const r = await getCrmContatos({
      search: debounced || undefined,
      nao_lidas: soNaoLidas ? 1 : undefined,
      per_page: 50,
    });
    if (r?.data) setContatos(r.data);
    setLoading(false);
  }, [debounced, soNaoLidas]);

  useEffect(() => { carregar(); }, [carregar, refreshKey]);

  // Polling silencioso da lista
  useEffect(() => {
    const t = setInterval(() => carregar(true), 6000);
    return () => clearInterval(t);
  }, [carregar]);

  return (
    <Box>
      <Flex gap={3} mb={4} flexWrap="wrap" align="center">
        <InputGroup maxW={{ base: "100%", md: "380px" }} flex="1">
          <InputLeftElement pointerEvents="none"><Search size={16} color="#9CA3AF" /></InputLeftElement>
          <Input
            placeholder="Buscar por nome ou telefone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            bg="white"
          />
        </InputGroup>
        <Button
          size="sm"
          variant={soNaoLidas ? "solid" : "outline"}
          colorScheme="orange"
          onClick={() => setSoNaoLidas((v) => !v)}
        >
          Não lidas
        </Button>
        <Box flex="1" />
        <Button
          size="sm" leftIcon={<Plus size={16} />}
          bg={ACCENT} color="white" _hover={{ bg: "#d4702a" }}
          onClick={novo.onOpen}
        >
          Novo contato
        </Button>
      </Flex>

      {loading ? (
        <Center py={16}><Spinner color={ACCENT} /></Center>
      ) : contatos.length === 0 ? (
        <Center py={16} flexDirection="column" color="gray.400">
          <MessageSquare size={40} />
          <Text mt={3} fontSize="sm">Nenhuma conversa ainda.</Text>
          <Text fontSize="xs">Inicie uma a partir das Oportunidades ou crie um contato.</Text>
        </Center>
      ) : (
        <VStack spacing={2} align="stretch">
          {contatos.map((c) => (
            <Flex
              key={c.id}
              p={3} borderRadius="lg" borderWidth="1px" borderColor="gray.100"
              bg="white" align="center" gap={3} cursor="pointer"
              _hover={{ borderColor: ACCENT, boxShadow: "sm" }}
              transition="all 0.12s"
              onClick={() => onAbrirChat(c.id)}
            >
              <Avatar size="sm" name={c.nome || formatPhone(c.telefone)} bg="gray.300" />
              <Box flex="1" minW={0}>
                <Flex align="center" gap={2}>
                  <Text fontWeight="semibold" fontSize="sm" isTruncated>
                    {c.nome || formatPhone(c.telefone)}
                  </Text>
                  {c.cliente && <Tag size="sm" colorScheme="green" flexShrink={0}>cliente</Tag>}
                </Flex>
                <Text fontSize="xs" color="gray.500" isTruncated>
                  {c.ultima_mensagem
                    ? `${c.ultima_mensagem.direcao === "enviada" ? "Você: " : ""}${c.ultima_mensagem.preview}`
                    : formatPhone(c.telefone)}
                </Text>
              </Box>
              <VStack spacing={1} align="end" flexShrink={0}>
                {c.ultima_mensagem?.em && (
                  <Text fontSize="10px" color="gray.400">{horaMsg(c.ultima_mensagem.em)}</Text>
                )}
                {c.nao_lidas > 0 && (
                  <Badge borderRadius="full" colorScheme="red" px={2}>{c.nao_lidas}</Badge>
                )}
              </VStack>
            </Flex>
          ))}
        </VStack>
      )}

      <NovoContatoModal
        isOpen={novo.isOpen}
        onClose={novo.onClose}
        onCriado={(id) => { carregar(true); onAbrirChat(id); }}
      />
    </Box>
  );
}
